import React, { useMemo } from 'react';
import { Persona } from '../../types/persona';
import { getCapabilityScore } from '../../utils/persona_engine_utils';

interface SkillCoverageChartProps {
  personas: Persona[];
  maxValue?: number;
}

export const SkillCoverageChart: React.FC<SkillCoverageChartProps> = ({
  personas,
  maxValue = 10
}) => {
  // Extract all unique capabilities from all personas
  const capabilities = useMemo(() => {
    const allCapabilities = new Set<string>();
    
    personas.forEach(persona => {
      Object.keys(persona.capabilities || {}).forEach(capability => {
        allCapabilities.add(capability);
      });
    });
    
    return Array.from(allCapabilities).sort();
  }, [personas]);
  
  // Calculate the average value for each capability
  const averageValues = useMemo(() => {
    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};
    
    personas.forEach(persona => {
      Object.entries(persona.capabilities || {}).forEach(([capability, value]) => {
        sums[capability] = (sums[capability] || 0) + getCapabilityScore(value);
        counts[capability] = (counts[capability] || 0) + 1;
      });
    });
    
    const averages: Record<string, number> = {};
    capabilities.forEach(capability => {
      averages[capability] = counts[capability] ? sums[capability] / counts[capability] : 0;
    });
    
    return averages;
  }, [personas, capabilities]);
  
  // Calculate the maximum value for each capability
  const maxValues = useMemo(() => {
    const maxes: Record<string, number> = {};
    
    personas.forEach(persona => {
      Object.entries(persona.capabilities || {}).forEach(([capability, value]) => {
        maxes[capability] = Math.max(maxes[capability] || 0, getCapabilityScore(value));
      });
    });
    
    return maxes;
  }, [personas]);
  
  // Calculate coverage percentage for each capability
  const coveragePercentages = useMemo(() => {
    const coverage: Record<string, number> = {};
    
    capabilities.forEach(capability => {
      const personasWithCapability = personas.filter(
        p => p.capabilities && getCapabilityScore(p.capabilities[capability]) > 0
      ).length;
      
      coverage[capability] = (personasWithCapability / personas.length) * 100;
    });
    
    return coverage;
  }, [personas, capabilities]);
  
  // Get color based on coverage percentage
  const getCoverageColor = (percentage: number) => {
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  // Get color based on value
  const getValueColor = (value: number) => {
    const percentage = (value / maxValue) * 100;
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  if (personas.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No personas in the team to analyze</p>
      </div>
    );
  }
  
  return (
    <div className="skill-coverage-chart">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-medium mb-4">Skill Coverage</h3>
          <div className="space-y-4">
            {capabilities.map(capability => (
              <div key={capability} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium capitalize">{capability}</span>
                  <span className="text-sm text-gray-600">
                    {Math.round(coveragePercentages[capability])}% Coverage
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                  <div 
                    className={`h-2.5 rounded-full ${getCoverageColor(coveragePercentages[capability])}`}
                    style={{ width: `${coveragePercentages[capability]}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Team Average</span>
                  <span className="text-xs text-gray-500">
                    {averageValues[capability].toFixed(1)}/{maxValue}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                  <div 
                    className={`h-1.5 rounded-full ${getValueColor(averageValues[capability])}`}
                    style={{ width: `${(averageValues[capability] / maxValue) * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Team Maximum</span>
                  <span className="text-xs text-gray-500">
                    {maxValues[capability]}/{maxValue}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full ${getValueColor(maxValues[capability])}`}
                    style={{ width: `${(maxValues[capability] / maxValue) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-medium mb-4">Team Skill Distribution</h3>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="space-y-6">
              {personas.map(persona => (
                <div key={persona.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold mr-2">
                      {persona.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-medium">{persona.name}</h4>
                      <p className="text-xs text-gray-500">{persona.primarySpecialization}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(persona.capabilities || {}).map(([capability, value]) => (
                      <div key={capability} className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-1.5" style={{ 
                          backgroundColor: `hsl(${
                            capabilities.indexOf(capability) * 137.5 % 360
                          }, 70%, 50%)` 
                        }}></div>
                        <span className="text-xs capitalize">{capability}: {getCapabilityScore(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium mb-3">Coverage Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total Skills Covered:</span>
                <span className="text-sm font-medium">{capabilities.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Average Coverage:</span>
                <span className="text-sm font-medium">
                  {Math.round(
                    capabilities.reduce((sum, cap) => sum + coveragePercentages[cap], 0) / 
                    capabilities.length
                  )}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Team Size:</span>
                <span className="text-sm font-medium">{personas.length} personas</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
